/* Art Bros – "An deiner Wand ansehen"
 * Baut aus dem Motivbild ein maßstabsgetreues 3D-Modell (glTF/GLB) des gewählten Formats
 * inkl. Rahmen direkt im Browser und zeigt es mit <model-viewer> (AR: Android WebXR, iOS Quick Look).
 */
const MV_SRC = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
const FRAMES = {
  'Schwarzer Rahmen': [0.10, 0.10, 0.10],
  'Weißer Rahmen': [0.93, 0.92, 0.90],
  'Holzoptik-Rahmen': [0.62, 0.45, 0.29],
};
const FRAME_W = 0.02;   // sichtbare Rahmenleiste (m)
const FRAME_D = 0.025;  // Rahmentiefe (m)
const PAPER_D = 0.002;  // Papierstärke ohne Rahmen (m)

function srgbToLinear(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }

/** Quader als 24 Vertices (je Fläche eigene Normalen). */
function box(x0, y0, z0, x1, y1, z1) {
  const p = [], n = [], idx = [];
  const faces = [
    [[0, 0, 1], [[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]]],
    [[0, 0, -1], [[x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]]],
    [[1, 0, 0], [[x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]]],
    [[-1, 0, 0], [[x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]]],
    [[0, 1, 0], [[x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0]]],
    [[0, -1, 0], [[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]]],
  ];
  faces.forEach(([nn, quad], f) => {
    quad.forEach((v) => { p.push(...v); n.push(...nn); });
    const b = f * 4; idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
  });
  return { p, n, idx };
}

function merge(parts) {
  const p = [], n = [], idx = []; let off = 0;
  parts.forEach((g) => { p.push(...g.p); n.push(...g.n); g.idx.forEach((i) => idx.push(i + off)); off += g.p.length / 3; });
  return { p, n, idx };
}

/**
 * @param {Uint8Array} jpeg  Motivbild als JPEG
 * @param {number} imgW, imgH Pixelmaße des Bildes
 * @param {number} w, h       Druckmaß in Metern
 * @param {number[]|null} frameRGB  sRGB 0..1 oder null (ohne Rahmen)
 */
export function buildGLB(jpeg, imgW, imgH, w, h, frameRGB) {
  const fw = frameRGB ? FRAME_W : 0;
  const depth = frameRGB ? FRAME_D : PAPER_D;
  const W = w + 2 * fw, H = h + 2 * fw;
  // Motiv: "cover" auf das Druckformat zuschneiden
  const ia = imgW / imgH, pa = w / h;
  let u0 = 0, u1 = 1, v0 = 0, v1 = 1;
  if (ia > pa) { const k = pa / ia; u0 = (1 - k) / 2; u1 = 1 - u0; } else { const k = ia / pa; v0 = (1 - k) / 2; v1 = 1 - v0; }
  const zA = depth + 0.0004;
  const art = {
    p: [-w / 2, -h / 2, zA, w / 2, -h / 2, zA, w / 2, h / 2, zA, -w / 2, h / 2, zA],
    n: [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1],
    uv: [u0, v1, u1, v1, u1, v0, u0, v0],
    idx: [0, 1, 2, 0, 2, 3],
  };
  let body;
  if (frameRGB) {
    body = merge([
      box(-W / 2, H / 2 - fw, 0, W / 2, H / 2, depth),
      box(-W / 2, -H / 2, 0, W / 2, -H / 2 + fw, depth),
      box(-W / 2, -H / 2 + fw, 0, -W / 2 + fw, H / 2 - fw, depth),
      box(W / 2 - fw, -H / 2 + fw, 0, W / 2, H / 2 - fw, depth),
      box(-w / 2, -h / 2, 0, w / 2, h / 2, 0.004), // Rückwand
    ]);
  } else {
    body = box(-w / 2, -h / 2, 0, w / 2, h / 2, depth);
  }
  const bodyColor = frameRGB ? frameRGB.map(srgbToLinear) : [0.95, 0.95, 0.94];

  // Binärpuffer zusammenbauen
  const chunks = []; let byteOffset = 0; const views = []; const accessors = [];
  function addView(typed, target) {
    const pad = (4 - (byteOffset % 4)) % 4; if (pad) { chunks.push(new Uint8Array(pad)); byteOffset += pad; }
    const bytes = new Uint8Array(typed.buffer, typed.byteOffset, typed.byteLength);
    views.push(Object.assign({ buffer: 0, byteOffset, byteLength: bytes.length }, target ? { target } : {}));
    chunks.push(bytes); byteOffset += bytes.length; return views.length - 1;
  }
  function addAcc(arr, type, comp, minmax, target) {
    const typed = comp === 5126 ? new Float32Array(arr) : new Uint16Array(arr);
    const bv = addView(typed, target);
    const size = { SCALAR: 1, VEC2: 2, VEC3: 3 }[type];
    const acc = { bufferView: bv, componentType: comp, count: arr.length / size, type };
    if (minmax) {
      const mn = [Infinity, Infinity, Infinity], mx = [-Infinity, -Infinity, -Infinity];
      for (let i = 0; i < arr.length; i += 3) for (let k = 0; k < 3; k++) { mn[k] = Math.min(mn[k], arr[i + k]); mx[k] = Math.max(mx[k], arr[i + k]); }
      acc.min = mn; acc.max = mx;
    }
    accessors.push(acc); return accessors.length - 1;
  }
  const aP = addAcc(art.p, 'VEC3', 5126, true, 34962), aN = addAcc(art.n, 'VEC3', 5126, false, 34962);
  const aT = addAcc(art.uv, 'VEC2', 5126, false, 34962), aI = addAcc(art.idx, 'SCALAR', 5123, false, 34963);
  const bP = addAcc(body.p, 'VEC3', 5126, true, 34962), bN = addAcc(body.n, 'VEC3', 5126, false, 34962);
  const bI = addAcc(body.idx, 'SCALAR', 5123, false, 34963);
  const imgView = addView(jpeg);

  const gltf = {
    asset: { version: '2.0', generator: 'Art Bros AR' },
    scene: 0, scenes: [{ nodes: [0, 1] }],
    nodes: [{ mesh: 0, name: 'Motiv' }, { mesh: 1, name: frameRGB ? 'Rahmen' : 'Papier' }],
    meshes: [
      { primitives: [{ attributes: { POSITION: aP, NORMAL: aN, TEXCOORD_0: aT }, indices: aI, material: 0 }] },
      { primitives: [{ attributes: { POSITION: bP, NORMAL: bN }, indices: bI, material: 1 }] },
    ],
    materials: [
      { name: 'Druck', pbrMetallicRoughness: { baseColorTexture: { index: 0 }, metallicFactor: 0, roughnessFactor: 0.9 } },
      { name: 'Rahmen', pbrMetallicRoughness: { baseColorFactor: [...bodyColor, 1], metallicFactor: 0, roughnessFactor: 0.6 } },
    ],
    textures: [{ source: 0, sampler: 0 }],
    samplers: [{ magFilter: 9729, minFilter: 9987, wrapS: 33071, wrapT: 33071 }],
    images: [{ bufferView: imgView, mimeType: 'image/jpeg' }],
    accessors, bufferViews: views, buffers: [{ byteLength: 0 }],
  };
  const padBin = (4 - (byteOffset % 4)) % 4; if (padBin) { chunks.push(new Uint8Array(padBin)); byteOffset += padBin; }
  gltf.buffers[0].byteLength = byteOffset;
  let json = new TextEncoder().encode(JSON.stringify(gltf));
  const padJ = (4 - (json.length % 4)) % 4;
  if (padJ) { const j = new Uint8Array(json.length + padJ); j.set(json); j.fill(0x20, json.length); json = j; }
  const total = 12 + 8 + json.length + 8 + byteOffset;
  const out = new Uint8Array(total); const dv = new DataView(out.buffer);
  dv.setUint32(0, 0x46546c67, true); dv.setUint32(4, 2, true); dv.setUint32(8, total, true);
  dv.setUint32(12, json.length, true); dv.setUint32(16, 0x4e4f534a, true); out.set(json, 20);
  let o = 20 + json.length;
  dv.setUint32(o, byteOffset, true); dv.setUint32(o + 4, 0x004e4942, true); o += 8;
  chunks.forEach((c) => { out.set(c, o); o += c.length; });
  return out;
}

/** Bild laden, auf max. 2048 px als JPEG neu kodieren (sicher für Quick Look/WebXR). */
async function loadJpeg(url) {
  const img = new Image(); img.crossOrigin = 'anonymous'; img.src = url; await img.decode();
  const pot = (v) => Math.min(2048, Math.pow(2, Math.round(Math.log2(v))));
  const cv = document.createElement('canvas'); cv.width = pot(img.naturalWidth); cv.height = pot(img.naturalHeight);
  cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height);
  const blob = await new Promise((r) => cv.toBlob(r, 'image/jpeg', 0.88));
  // Seitenverhältnis des Originals zurückgeben – die Textur selbst ist gestreckt (Zweierpotenz)
  return { bytes: new Uint8Array(await blob.arrayBuffer()), w: img.naturalWidth, h: img.naturalHeight };
}

function loadViewer() {
  if (customElements.get('model-viewer')) return Promise.resolve();
  return new Promise((res, rej) => { const s = document.createElement('script'); s.type = 'module'; s.src = MV_SRC; s.onload = res; s.onerror = rej; document.head.appendChild(s); })
    .then(() => customElements.whenDefined('model-viewer'));
}

const checked = (name) => document.querySelector('input[data-option-name="' + name + '"]:checked')?.value || '';

async function open(btn) {
  const dlg = document.querySelector('[data-ab-ar-dialog]'); if (!dlg) return;
  const size = checked('Größe') || btn.dataset.defaultSize || '50 × 70 cm';
  const finish = checked('Ausführung') || 'Ohne Rahmen';
  const m = size.match(/(\d+)\s*[x×]\s*(\d+)/); if (!m) return;
  const w = +m[1] / 100, h = +m[2] / 100;
  dlg.querySelector('[data-ab-ar-label]').textContent = size + ' · ' + finish;
  dlg.showModal(); dlg.classList.add('is-loading');
  try {
    const [img] = await Promise.all([loadJpeg(btn.dataset.image), loadViewer()]);
    const glb = buildGLB(img.bytes, img.w, img.h, w, h, FRAMES[finish] || null);
    const url = URL.createObjectURL(new Blob([glb], { type: 'model/gltf-binary' }));
    const mv = dlg.querySelector('model-viewer');
    if (mv.dataset.url) URL.revokeObjectURL(mv.dataset.url);
    mv.dataset.url = url; mv.src = url;
    mv.addEventListener('load', () => dlg.classList.remove('is-loading'), { once: true });
  } catch (e) { console.error(e); dlg.classList.remove('is-loading'); dlg.classList.add('is-error'); }
}

if (typeof document !== 'undefined') document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('[data-ab-ar-open]'); if (btn) { e.preventDefault(); open(btn); return; }
  const dlg = e.target.closest?.('[data-ab-ar-dialog]');
  if (dlg && (e.target === dlg || e.target.closest('[data-ab-ar-close]'))) dlg.close();
  const go = e.target.closest?.('[data-ab-ar-go]');
  if (go) { const mv = go.closest('[data-ab-ar-dialog]').querySelector('model-viewer'); mv.canActivateAR ? mv.activateAR() : go.closest('[data-ab-ar-dialog]').classList.add('no-ar'); }
});
